import { css, cx } from "@emotion/css";
import { Checkbox, tableBorder, tableStyles } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import { borderRadius, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDApplicationResource, ArgoCDApplicationSourceResource } from "@octopusdeploy/octopus-server-client";
import React, { useCallback } from "react";
import { MissingSourceNameWarning } from "~/areas/infrastructure/components/ArgoCDInstances/components/GenerateArgoCDAnnotationsDrawer/components/MissingSourceNameWarning";
import { SourceTypeChip } from "~/areas/infrastructure/components/ArgoCDInstances/components/SourceTypeChip";
import { sharedStyles } from "../sharedStyles";
const styles = {
    table: css({
        border: tableBorder,
        borderRadius: borderRadius["large"],
    }),
    unnamed: css({
        color: themeTokens.color.text.secondary,
    }),
    checkboxOverride: css({
        // Currently checkboxes don't support no label, so this removes the gap to the empty label
        "> div": {
            gap: 0,
        },
    }),
    onePercCol: css({
        width: "1%",
    }),
    nameCol: css({
        width: "360px",
    }),
};
type SourceSelectionTableProps = {
    application: ArgoCDApplicationResource | undefined;
    selectedSources: ArgoCDApplicationSourceResource[];
    setSelectedSources: (selectedSources: ArgoCDApplicationSourceResource[]) => void;
};
export const SourceSelectionTable = (props: SourceSelectionTableProps) => {
    const { application, selectedSources, setSelectedSources } = props;
    const onSelectedChanged = (isSelected: boolean, source: ArgoCDApplicationSourceResource) => {
        if (isSelected) {
            setSelectedSources([...selectedSources, source]);
        }
        else {
            const updated = [...selectedSources];
            const idx = updated.findIndex((src) => src.Index === source.Index);
            if (idx >= 0) {
                updated.splice(idx, 1);
            }
            setSelectedSources(updated);
        }
    };
    const isSourceSelected = useCallback((index: number) => selectedSources.some((src) => src.Index === index), [selectedSources]);
    const renderSourceName = (source: ArgoCDApplicationSourceResource) => {
        const isSelected = isSourceSelected(source.Index);
        if (StringHelper.isNullOrWhiteSpace(source.Name)) {
            if (isSelected) {
                return <MissingSourceNameWarning showHelpLink={true}/>;
            }
            else {
                return <span className={styles.unnamed}>Unnamed</span>;
            }
        }
        return source.Name;
    };
    return (<div>
            <h2 className={sharedStyles.header}>2. Select sources</h2>
            <p>Select the sources you want Octopus to update during a deployment.</p>
            <table className={cx(tableStyles, styles.table)}>
                <colgroup>
                    <col className={styles.onePercCol}/>
                    <col className={styles.nameCol}/>
                    <col />
                    <col className={styles.onePercCol}/>
                </colgroup>
                <thead>
                    <tr>
                        <th />
                        <th>Source Name</th>
                        <th>Repo URL</th>
                        <th>Type</th>
                    </tr>
                </thead>
                <tbody>
                    {(application?.Sources || []).map((src) => (<tr key={src.Index}>
                            <td>
                                <Checkbox value={isSourceSelected(src.Index)} label={""} onChange={(isSelected) => onSelectedChanged(isSelected, src)} noMargin className={styles.checkboxOverride}/>
                            </td>
                            <td>{renderSourceName(src)}</td>
                            <td>{src.RepositoryUrl}</td>
                            <td>
                                <SourceTypeChip source={src}/>
                            </td>
                        </tr>))}
                </tbody>
            </table>
        </div>);
};
